/**
 * ContributorTraining — Onboarding walkthrough for new translators.
 *
 * Three modules (~10 minutes total):
 *   1. What you're looking at (the system)
 *   2. How to judge (the workflow)
 *   3. What makes this different (attestations, audit)
 *
 * Completion: acknowledge understanding → certificate generated.
 * Gate: must complete before accessing review UI.
 *
 * Route: /polyglot/training
 * API: POST /@polyglot/contributors/me/training
 *
 * WI-TRN-002
 */
import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Step,
  Icon,
  Button,
  Message,
  Checkbox,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';

// --- Module Content ---

const MODULES = [
  {
    key: 'system',
    title: 'The Big Picture',
    icon: 'world',
    content: (
      <div>
        <p style={{ fontSize: '1.15em', lineHeight: '1.7', fontWeight: '500' }}>
          You're about to help translate software that runs in <strong>71 languages</strong> around
          the world. That's not a small thing.
        </p>
        <p style={{ fontSize: '1.05em', lineHeight: '1.7' }}>
          The <strong>Polyglot Translation Project</strong> manages <strong>330,000+ translations</strong> for
          the Plone and Volto ecosystem — open-source software used by organisations on every continent.
          When you improve a translation, real people in your language community get a better experience.
        </p>

        <div style={{ margin: '1.5em 0', padding: '1em 1.2em', background: '#f0f7ff', borderRadius: '8px', border: '1px solid #d0e4f7' }}>
          <p style={{ fontSize: '1.05em', lineHeight: '1.7', margin: 0 }}>
            <strong>This is not a typical translation tool.</strong> You won't stare at empty text boxes.
            Most translations already exist — some from professional community teams (RedTurtle for Italian,
            kitconcept for German, CodeSyntax for Basque), others generated by AI. Your role is to
            <strong>review what's already there</strong>, bring your native-speaker judgement, and make it better.
            You are the expert the machine can't replace.
          </p>
        </div>

        <p style={{ fontSize: '1.15em', lineHeight: '1.7', fontWeight: '500', marginTop: '1.5em' }}>
          You won't be working alone. The system gives you more context per string than any translation tool.
        </p>

        <div style={{ margin: '1em 0', padding: '1em 1.2em', background: '#f8f8f8', borderRadius: '8px', borderLeft: '4px solid #2185d0' }}>
          <div style={{ fontWeight: '600', color: '#2185d0', marginBottom: '0.3em' }}>ISO 704 Concept Definitions</div>
          <p style={{ margin: 0, lineHeight: '1.6' }}>
            "Delete" can mean removing a file, removing a comment, or clearing a field. For 1,431 strings,
            we've documented <em>exactly</em> what each one means in the product — inspected from source code.
            The concept definition tells you what the user sees and does, so your translation conveys the right meaning.
          </p>
        </div>

        <div style={{ margin: '1em 0', padding: '1em 1.2em', background: '#f8f8f8', borderRadius: '8px', borderLeft: '4px solid #00b5ad' }}>
          <div style={{ fontWeight: '600', color: '#00b5ad', marginBottom: '0.3em' }}>Sibling Translations</div>
          <p style={{ margin: 0, lineHeight: '1.6' }}>
            How did Swedish, Danish, and Norwegian translate this same string? If they all agree and
            your language says something different, that's worth noticing. Sibling and language family
            translations are shown automatically — cross-referencing without manual searching.
          </p>
        </div>

        <div style={{ margin: '1em 0', padding: '1em 1.2em', background: '#f8f8f8', borderRadius: '8px', borderLeft: '4px solid #6435c9' }}>
          <div style={{ fontWeight: '600', color: '#6435c9', marginBottom: '0.3em' }}>Language Conventions</div>
          <p style={{ margin: 0, lineHeight: '1.6' }}>
            Each language has documented rules — formal or informal register, how buttons should be
            labelled (infinitive, imperative, noun?), script and character conventions. These aren't
            guesses — they're researched per-language standards that keep translations consistent.
          </p>
        </div>

        <div style={{ margin: '1em 0', padding: '1em 1.2em', background: '#f8f8f8', borderRadius: '8px', borderLeft: '4px solid #fbbd08' }}>
          <div style={{ fontWeight: '600', color: '#b58105', marginBottom: '0.3em' }}>Terminology Glossary</div>
          <p style={{ margin: 0, lineHeight: '1.6' }}>
            193,000 reference translations from official EU sources (IATE). When you're translating
            "workflow" or "metadata", the glossary shows how the EU's professional translators rendered
            it in your language. Not a rule — a reference. Your judgement decides.
          </p>
        </div>

        <div style={{ margin: '1em 0', padding: '1em 1.2em', background: '#f8f8f8', borderRadius: '8px', borderLeft: '4px solid #21ba45' }}>
          <div style={{ fontWeight: '600', color: '#21ba45', marginBottom: '0.3em' }}>Quality Signals</div>
          <p style={{ margin: 0, lineHeight: '1.6' }}>
            AI has already scanned every translation for common issues — wrong meaning, missing
            placeholders, incomplete text, register mismatches. When a finding exists, you'll see it
            alongside the translation with a suggested fix. Back-translation verification shows whether
            the meaning survived the translation round-trip.
          </p>
        </div>

        <div style={{ margin: '1em 0', padding: '1em 1.2em', background: '#f8f8f8', borderRadius: '8px', borderLeft: '4px solid #888' }}>
          <div style={{ fontWeight: '600', color: '#555', marginBottom: '0.3em' }}>Full History</div>
          <p style={{ margin: 0, lineHeight: '1.6' }}>
            Every correction ever made to this string, every review, every note left by another
            contributor, every attestation. You're not the first person to look at this string —
            and you can see everything they did and said about it.
          </p>
        </div>

        <p style={{ fontSize: '1.05em', lineHeight: '1.7', marginTop: '1.5em', color: '#555' }}>
          All of this context appears <strong>automatically</strong> for each string you review. You don't
          search for it — it's there when you need it, hidden when it's not relevant.
        </p>
      </div>
    ),
  },
  {
    key: 'workflow',
    title: 'Your Toolkit',
    icon: 'hand paper',
    content: (
      <div>
        <p style={{ fontSize: '1.15em', lineHeight: '1.7', fontWeight: '500' }}>
          You have four tools. Use whichever fits the moment.
        </p>

        <div style={{ margin: '1.2em 0', padding: '1em 1.2em', background: '#f0faf0', borderRadius: '8px', borderLeft: '4px solid #21ba45' }}>
          <div style={{ fontSize: '1.1em', fontWeight: '600', color: '#21ba45', marginBottom: '0.3em' }}>
            Looks good
          </div>
          <p style={{ margin: 0, lineHeight: '1.7' }}>
            You read the English, you read the translation, it sounds right. Natural. A native speaker
            would get it instantly. <strong>Check the box and submit.</strong> This is the most common
            action — and it's one of the most valuable. Every "looks good" is a qualified human confirming
            that an AI translation actually works. That confirmation is what makes the whole system trustworthy.
          </p>
        </div>

        <div style={{ margin: '1.2em 0', padding: '1em 1.2em', background: '#fff8f0', borderRadius: '8px', borderLeft: '4px solid #f2711c' }}>
          <div style={{ fontSize: '1.1em', fontWeight: '600', color: '#f2711c', marginBottom: '0.3em' }}>
            Needs work
          </div>
          <p style={{ margin: 0, lineHeight: '1.7' }}>
            Something is off. Maybe the translation is technically correct but sounds robotic. Maybe
            it uses the wrong level of formality — a warning message that sounds like a casual chat,
            or a button label that reads like a legal document. Maybe it's just the English left
            untranslated. <strong>Check the box and submit.</strong> You don't have to fix it yourself — flagging
            it is already a contribution. Strings flagged as "needs work" get priority attention.
          </p>
        </div>

        <div style={{ margin: '1.2em 0', padding: '1em 1.2em', background: '#f0f7ff', borderRadius: '8px', borderLeft: '4px solid #2185d0' }}>
          <div style={{ fontSize: '1.1em', fontWeight: '600', color: '#2185d0', marginBottom: '0.3em' }}>
            Edit
          </div>
          <p style={{ margin: 0, lineHeight: '1.7' }}>
            You know the right translation. Click the text, type your version, add a brief reason if
            it's not obvious ("formal register" or "matches Settings page terminology"). The system
            validates your correction automatically — checking placeholders, length ratios, glossary
            consistency — and applies it if everything checks out.
          </p>
        </div>

        <div style={{ margin: '1.2em 0', padding: '1em 1.2em', background: '#f5f5f5', borderRadius: '8px', borderLeft: '4px solid #888' }}>
          <div style={{ fontSize: '1.1em', fontWeight: '600', color: '#555', marginBottom: '0.3em' }}>
            Comment
          </div>
          <p style={{ margin: 0, lineHeight: '1.7' }}>
            You notice something but aren't sure of the fix. Leave a note — "Should this match the
            term on the Settings page?", "This sounds too formal for a button", "Can a native speaker
            double-check?". Comments help the next person who looks at this string. They build collective
            knowledge.
          </p>
        </div>

        <p style={{ fontSize: '1.05em', lineHeight: '1.7', marginTop: '1em', color: '#555' }}>
          <strong>Skip</strong> is always an option. If you're not sure, move on. No one is keeping
          score on how many you skip.
        </p>
      </div>
    ),
  },
  {
    key: 'attestation',
    title: 'Why Your Name Matters',
    icon: 'certificate',
    content: (
      <div>
        <p style={{ fontSize: '1.15em', lineHeight: '1.7', fontWeight: '500' }}>
          This isn't a suggestion box. When you vote, you're signing your name.
        </p>

        <p style={{ fontSize: '1.05em', lineHeight: '1.7' }}>
          Every vote — whether "looks good" or "needs work" — creates a <strong>formal review
          attestation</strong>. That's a record that says: "I, {'{your name}'}, a qualified speaker
          of {'{your language}'}, examined this translation on {'{this date}'} and recorded my
          professional judgement."
        </p>

        <div style={{ margin: '1.5em 0', padding: '1em 1.2em', background: '#fffde7', borderRadius: '8px', border: '1px solid #ffe082' }}>
          <p style={{ fontSize: '1.05em', lineHeight: '1.7', margin: 0 }}>
            <strong>Why does this matter?</strong> Because AI translated most of these strings.
            AI is fast but it can't know if a translation <em>feels</em> right to a native speaker.
            Your attestation is the bridge between "a machine produced this" and "a human confirmed it."
            That bridge is what makes these translations trustworthy.
          </p>
        </div>

        <p style={{ fontSize: '1.05em', lineHeight: '1.7' }}>
          The system keeps a <strong>snapshot</strong> of exactly what you reviewed. If someone later
          corrects the translation, your attestation doesn't silently cover the new version — it's
          marked as "stale" because you attested to different text. Honesty is built into the system.
        </p>

        <p style={{ fontSize: '1.05em', lineHeight: '1.7' }}>
          Your contributions are dedicated to the <strong>public domain</strong> (CC0). Free for everyone,
          forever. You are <strong>held harmless</strong> — your obligation is simply your honest
          best effort.
        </p>

        <div style={{ margin: '1.5em 0', padding: '1.2em', background: 'linear-gradient(135deg, #e8f4fd 0%, #f0faf0 100%)', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ fontSize: '1.2em', lineHeight: '1.7', margin: 0, fontWeight: '500' }}>
            Your name on your work isn't pressure.
            <br />
            <strong>It's respect.</strong>
          </p>
        </div>
      </div>
    ),
  },
];

// === MAIN COMPONENT ===

export default function ContributorTraining() {
  const history = useHistory();
  const [currentModule, setCurrentModule] = useState(0);
  const [completedModules, setCompletedModules] = useState(new Set());
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [certificateId, setCertificateId] = useState(null);

  // Check if already trained
  const [alreadyTrained, setAlreadyTrained] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkStatus() {
      try {
        const data = await fetchApi(`${getApiPath()}/@polyglot/contributors/me`);
        if (data.training_completed_at) {
          setAlreadyTrained(true);
        }
      } catch {
        // Not a contributor yet — that's fine, they'll hit the gate elsewhere
      }
      setLoading(false);
    }
    checkStatus();
  }, []);

  const markModuleRead = () => {
    setCompletedModules((prev) => {
      const next = new Set(prev);
      next.add(currentModule);
      return next;
    });
  };

  const goNext = () => {
    markModuleRead();
    if (currentModule < MODULES.length - 1) {
      setCurrentModule(currentModule + 1);
      window.scrollTo(0, 0);
    }
  };

  const goPrev = () => {
    if (currentModule > 0) {
      setCurrentModule(currentModule - 1);
      window.scrollTo(0, 0);
    }
  };

  const allRead = completedModules.size >= MODULES.length;

  const handleComplete = async () => {
    if (!acknowledged || !allRead || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await fetchApi(
        `${getApiPath()}/@polyglot/contributors/me/training`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            module: 'onboarding',
            version: 'WI-TRN-002-v0.2.0',
          }),
        },
      );
      setCompleted(true);
      setCertificateId(result.certificate_id || null);
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Container style={{ paddingTop: '2em', maxWidth: '800px' }}>
        <Segment loading placeholder style={{ minHeight: '200px' }} />
      </Container>
    );
  }

  // Already trained — show content in read-only review mode
  if (alreadyTrained && !completed) {
    return (
      <Container style={{ paddingTop: '2em', maxWidth: '800px', paddingBottom: '3em' }}>
        <Helmet title="Onboarding Training" />

        <Message positive icon style={{ marginBottom: '1.5em' }}>
          <Icon name="certificate" />
          <Message.Content>
            <Message.Header>Training completed</Message.Header>
            <p>You completed onboarding. This page is available for reference anytime.
              Go to your <Link to="/polyglot/dashboard">dashboard</Link> to continue reviewing.</p>
          </Message.Content>
        </Message>

        <Header as="h1">
          <Icon name="translate" />
          <Header.Content>
            Onboarding Training
            <Header.Subheader>Reference — read anytime</Header.Subheader>
          </Header.Content>
        </Header>

        {MODULES.map((mod, i) => (
          <Segment key={mod.key} raised style={{ padding: '2em', marginBottom: '1em' }}>
            <Header as="h2">
              <Icon name={mod.icon} />
              <Header.Content>
                <span style={{ color: '#888', marginRight: '0.5em' }}>{i + 1}.</span>
                {mod.title}
              </Header.Content>
            </Header>
            {mod.content}
          </Segment>
        ))}
      </Container>
    );
  }

  // Completion screen
  if (completed) {
    return (
      <Container style={{ paddingTop: '2em', maxWidth: '800px' }}>
        <Helmet title="Welcome, Official Translator" />
        <Segment textAlign="center" style={{ padding: '3em' }}>
          <Icon name="certificate" size="huge" color="yellow" />
          <Header as="h1" style={{ marginTop: '0.5em' }}>
            Congratulations!
            <Header.Subheader style={{ fontSize: '0.5em', marginTop: '0.5em' }}>
              You are now an Official Polyglot Translator
            </Header.Subheader>
          </Header>
          <p style={{ fontSize: '1.1em', color: '#555', maxWidth: '500px', margin: '1em auto' }}>
            Your certificate has been generated. You can now start reviewing translations
            for your assigned languages.
          </p>
          <div style={{ display: 'flex', gap: '1em', justifyContent: 'center', marginTop: '2em', flexWrap: 'wrap' }}>
            <Button color="yellow" size="large" as={Link} to="/polyglot/certificate">
              <Icon name="certificate" /> View Certificate
            </Button>
            <Button primary size="large" as={Link} to="/polyglot/dashboard">
              <Icon name="arrow right" /> Go to Dashboard
            </Button>
          </div>
        </Segment>
      </Container>
    );
  }

  const mod = MODULES[currentModule];

  return (
    <Container style={{ paddingTop: '2em', maxWidth: '800px', paddingBottom: '3em' }}>
      <Helmet title="Translator Onboarding" />

      <Header as="h1">
        <Icon name="translate" />
        <Header.Content>
          Welcome to Polyglot
          <Header.Subheader>Three short reads, then you're an Official Polyglot Translator</Header.Subheader>
        </Header.Content>
      </Header>

      {/* Step indicators */}
      <Step.Group ordered fluid size="small" style={{ marginBottom: '1.5em' }}>
        {MODULES.map((m, i) => (
          <Step
            key={m.key}
            active={i === currentModule}
            completed={completedModules.has(i)}
            onClick={() => setCurrentModule(i)}
            style={{ cursor: 'pointer' }}
          >
            <Step.Content>
              <Step.Title>{m.title}</Step.Title>
            </Step.Content>
          </Step>
        ))}
      </Step.Group>

      {/* Module content */}
      <Segment raised style={{ padding: '2em' }}>
        <Header as="h2">
          <Icon name={mod.icon} />
          <Header.Content>{mod.title}</Header.Content>
        </Header>
        {mod.content}
      </Segment>

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1em' }}>
        <Button basic disabled={currentModule === 0} onClick={goPrev}>
          <Icon name="arrow left" /> Previous
        </Button>
        {currentModule < MODULES.length - 1 ? (
          <Button primary onClick={goNext}>
            Next <Icon name="arrow right" />
          </Button>
        ) : (
          <Button primary onClick={markModuleRead} disabled={completedModules.has(currentModule)}>
            <Icon name="check" /> Mark as Read
          </Button>
        )}
      </div>

      {/* Acknowledgement + Complete (only after all modules read) */}
      {allRead && (
        <Segment style={{ marginTop: '1.5em' }}>
          <Header as="h3">
            <Icon name="handshake" />
            <Header.Content>Ready to begin</Header.Content>
          </Header>
          <div style={{ marginBottom: '1em' }}>
            <Checkbox
              checked={acknowledged}
              onChange={(_, { checked }) => setAcknowledged(checked)}
              label="I understand that my reviews are formal attestations recorded with my name, and that this project is subject to audit. I have read and accept the Translation Contributor Policy and Contributors Agreement."
              style={{ fontSize: '1em', lineHeight: '1.6' }}
            />
          </div>
          {error && <Message negative>{error}</Message>}
          <Button
            color="green"
            size="large"
            disabled={!acknowledged || submitting}
            loading={submitting}
            onClick={handleComplete}
          >
            <Icon name="certificate" /> Training Completed — Get Certificate
          </Button>
        </Segment>
      )}
    </Container>
  );
}
